import React, { useState, useRef, useEffect } from 'react';
import { Employee, Tool, TrainingRecord, AppSettings, Notification } from '../types';
import { Plus, Trash2, User, Search, Mail, Briefcase, LayoutGrid, List, X, ShieldCheck, Laptop, GraduationCap, CheckCircle2, Download, Filter, ArrowUpDown, FileText, AlertCircle, Bell, Save, Send, KeyRound, Shield, CheckCircle, Clock } from 'lucide-react';
import { EmptyState } from '../components/EmptyState';
import { CsvImporter } from '../components/CsvImporter';
import { extractEmailDomain, getCurrentUserEmailDomain, inviteEmployee, resetPasswordForEmail } from '../services/authService';
import { InfoTooltip } from '../components/InfoTooltip';

interface EmployeeViewProps {
  employees: Employee[];
  tools: Tool[];
  onAdd: (employee: Employee) => void;
  onUpdate: (employee: Employee) => void;
  onDelete: (id: string) => void;
  onUpdateTool: (tool: Tool) => void;
  trainingRecords: TrainingRecord[];
  settings: AppSettings;
  notifications: Notification[];
}

type SortField = 'name' | 'role' | 'team' | 'toolsCount';

const buildFullName = (firstName?: string, lastName?: string, fallback?: string) =>
  [firstName?.trim(), lastName?.trim()].filter(Boolean).join(' ').trim() || (fallback ?? '').trim();

const splitFullName = (fullName: string): { firstName: string; lastName: string } => {
  const parts = (fullName || '').trim().split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] ?? '',
    lastName: parts.slice(1).join(' '),
  };
};

interface EmployeeModalProps {
  employee: Employee;
  onClose: () => void;
  employees: Employee[];
  tools: Tool[];
  trainingRecords: TrainingRecord[];
  settings: AppSettings;
  notifications: Notification[];
  onUpdate: (employee: Employee) => void;
  onUpdateTool: (tool: Tool) => void;
}

const AccountAccessSection: React.FC<{
  employee: Employee;
  form: Employee;
  settings: AppSettings;
  updateEmployee: (field: keyof Employee, value: any) => void;
  onUpdate: (emp: Employee) => void;
}> = ({ employee, form, settings, updateEmployee, onUpdate }) => {
  const [inviting, setInviting] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [actionMsg, setActionMsg] = useState('');

  const hasAccount = !!employee.userId;
  const status: 'active' | 'invited' | 'not_invited' = hasAccount
    ? 'active'
    : employee.invitedAt
    ? 'invited'
    : 'not_invited';

  const statusBadge = {
    active: { label: 'Active', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    invited: { label: 'Invited (pending)', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
    not_invited: { label: 'Not invited', cls: 'bg-slate-100 text-slate-500 border-slate-200' },
  }[status];

  const handleInvite = async () => {
    if (!employee.email) { setActionMsg('Email is required to invite.'); return; }
    setInviting(true);
    setActionMsg('');
    try {
      const recipientDomain = extractEmailDomain(employee.email);
      const configuredDomains = (settings.allowedEmailDomains || []).map((d) => d.toLowerCase());
      const currentUserDomain = await getCurrentUserEmailDomain();
      const baselineDomain = configuredDomains.length > 0 ? null : currentUserDomain;
      const isExternal = configuredDomains.length > 0
        ? !!recipientDomain && !configuredDomains.includes(recipientDomain.toLowerCase())
        : !!recipientDomain && !!baselineDomain && recipientDomain !== baselineDomain;
      if (isExternal) {
        const orgDomainsLabel = configuredDomains.length > 0
          ? configuredDomains.join(', ')
          : (baselineDomain || 'not configured');
        const shouldContinue = window.confirm(
          `External domain detected.\n\nEmployee email domain: ${recipientDomain}\nInternal domain(s): ${orgDomainsLabel}\n\nDo you want to continue with this invitation?`
        );
        if (!shouldContinue) {
          setActionMsg('Invitation cancelled.');
          return;
        }
        setActionMsg(`Warning: external domain detected (${recipientDomain}) vs internal (${orgDomainsLabel}).`);
      }
      const fullName = buildFullName(form.firstName, form.lastName, form.name || employee.name || employee.email);
      const { firstName, lastName } = splitFullName(fullName);
      const authUserId = await inviteEmployee(employee.email, {
        firstName,
        lastName: lastName || undefined,
        name: fullName,
        role: (form.authRole || employee.authRole) === 'admin' ? 'admin' : 'user',
        team: (form.team || employee.team || '').trim() || undefined,
      });
      onUpdate({ ...employee, userId: authUserId, invitedAt: new Date().toISOString(), authRole: form.authRole || 'user' });
      setActionMsg('Invitation sent!');
    } catch (err: any) {
      setActionMsg(err?.message || 'Failed to invite.');
    } finally {
      setInviting(false);
    }
  };

  const handleResetPassword = async () => {
    if (!employee.email) return;
    setResetting(true);
    setActionMsg('');
    try {
      await resetPasswordForEmail(employee.email);
      setActionMsg('Password reset email sent!');
    } catch (err: any) {
      setActionMsg(err?.message || 'Failed to reset password.');
    } finally {
      setResetting(false);
    }
  };

  return (
    <div>
      <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-3 flex items-center gap-2">
        <Shield size={16} className="text-indigo-600" />
        Account & Access
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-slate-500 mb-1">Platform role</label>
          <select
            className="w-full p-2 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500 text-sm bg-white"
            value={form.authRole || 'user'}
            onChange={(e) => updateEmployee('authRole', e.target.value as 'admin' | 'user')}
          >
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Account status</label>
          <div className="flex items-center gap-2 mt-1">
            <span className={`inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full border ${statusBadge.cls}`}>
              {statusBadge.label}
            </span>
            {employee.lastLoginAt && (
              <span className="text-xs text-slate-400">
                Last login: {new Date(employee.lastLoginAt).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-4 flex-wrap">
        {!hasAccount && (
          <button
            type="button"
            onClick={handleInvite}
            disabled={inviting}
            className="flex items-center gap-2 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
          >
            <Send size={14} />
            {inviting ? 'Sending...' : 'Invite'}
          </button>
        )}
        {hasAccount && (
          <button
            type="button"
            onClick={handleResetPassword}
            disabled={resetting}
            className="flex items-center gap-2 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium transition-colors"
          >
            <KeyRound size={14} />
            {resetting ? 'Sending...' : 'Reset password'}
          </button>
        )}
        {actionMsg && (
          <span className="text-xs text-slate-600 ml-2">{actionMsg}</span>
        )}
      </div>
    </div>
  );
};

const EmployeeModal: React.FC<EmployeeModalProps> = ({ employee, onClose, employees, tools, trainingRecords, settings, notifications, onUpdate, onUpdateTool }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'reports'>('overview');
  const [form, setForm] = useState<Employee>(() => {
    const parsed = splitFullName(employee.name || '');
    return {
      ...employee,
      firstName: employee.firstName ?? parsed.firstName,
      lastName: employee.lastName ?? parsed.lastName,
    };
  });
  const formRef = useRef<Employee>(form);
  formRef.current = form;

  useEffect(() => {
    const parsed = splitFullName(employee.name || '');
    setForm({
      ...employee,
      firstName: employee.firstName ?? parsed.firstName,
      lastName: employee.lastName ?? parsed.lastName,
    });
  }, [employee]);

  const assignedTools = tools.filter(t => t.assignedUserIds?.includes(employee.id) || t.powerUserId === employee.id);
  const directReports = employees.filter(e => e.managerId === employee.id);

  const isDirty = JSON.stringify(form) !== JSON.stringify(employee);
  const canSave =
    (form.firstName ?? '').trim().length > 0 &&
    (form.lastName ?? '').trim().length > 0 &&
    (form.email ?? '').trim().length > 0;

  const handleSave = () => {
    if (!canSave) {
      alert('First name, last name and email are required.');
      return;
    }
    const next = { ...formRef.current };
    next.name = buildFullName(next.firstName, next.lastName, next.name);
    onUpdate(next);
  };

  const updateEmployee = (field: keyof Employee, value: any) => {
    const next = { ...form, [field]: value ?? undefined };
    setForm(next);
  };

  const handleClose = () => {
    if (isDirty && !canSave) {
      alert('First name, last name and email are required to save. Fix the form or discard changes.');
      return;
    }
    if (isDirty && canSave && !window.confirm('You have unsaved changes. Discard?')) {
      return;
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4 animate-fade-in">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex justify-between items-center p-6 border-b border-slate-100 bg-slate-50">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center font-bold text-lg border-2 border-white shadow-sm">
              {(form.firstName || form.name || '?').charAt(0)}
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">{buildFullName(form.firstName, form.lastName, form.name) || '—'}</h2>
              <p className="text-sm text-slate-500">{form.role} &bull; {form.email || '—'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={!canSave}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors"
            >
              <Save size={18} />
              Save
            </button>
            <button onClick={handleClose} className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100" title="Close">
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="flex border-b border-slate-100 bg-white px-6">
          <button 
            onClick={() => setActiveTab('overview')}
            className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${activeTab === 'overview' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          >
            Overview
          </button>
          <button 
            onClick={() => setActiveTab('reports')}
            className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'reports' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          >
            Direct Reports
            {directReports.length > 0 && <span className="bg-slate-200 text-slate-600 text-[10px] px-1.5 rounded-full">{directReports.length}</span>}
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6 space-y-8 bg-white">
          {activeTab === 'overview' && (
            <>
              <div>
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-2 flex items-center gap-2">
                  <User size={16} className="text-slate-400" />
                  Details & Reporting
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="edit-emp-first-name" className="block text-xs text-slate-500 mb-1">First name</label>
                    <input
                      id="edit-emp-first-name"
                      type="text"
                      className="w-full p-2 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                      value={form.firstName || ''}
                      onChange={(e) => updateEmployee('firstName', e.target.value)}
                      placeholder="Jane"
                    />
                  </div>
                  <div>
                    <label htmlFor="edit-emp-last-name" className="block text-xs text-slate-500 mb-1">Last name</label>
                    <input
                      id="edit-emp-last-name"
                      type="text"
                      className="w-full p-2 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                      value={form.lastName || ''}
                      onChange={(e) => updateEmployee('lastName', e.target.value)}
                      placeholder="Doe"
                    />
                  </div>
                  <div>
                    <label htmlFor="edit-emp-email" className="block text-xs text-slate-500 mb-1">Email</label>
                    <input
                      id="edit-emp-email"
                      type="email"
                      className="w-full p-2 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                      value={form.email || ''}
                      onChange={(e) => updateEmployee('email', e.target.value)}
                      placeholder="email@company.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="edit-emp-role" className="block text-xs text-slate-500 mb-1">Role</label>
                    <input
                      id="edit-emp-role"
                      type="text"
                      className="w-full p-2 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                      value={form.role || ''}
                      onChange={(e) => updateEmployee('role', e.target.value)}
                      placeholder="e.g. Developer, Manager"
                    />
                  </div>
                  <div>
                    <label htmlFor="edit-emp-team" className="block text-xs text-slate-500 mb-1">Team</label>
                    <select 
                      id="edit-emp-team"
                      className="w-full p-2 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                      value={form.team || ''}
                      onChange={(e) => updateEmployee('team', e.target.value)}
                    >
                      <option value="">-- Select Team --</option>
                      {(settings?.teams || []).map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Direct Manager</label>
                    <select 
                      className="w-full p-2 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                      value={form.managerId || ''}
                      onChange={(e) => updateEmployee('managerId', e.target.value)}
                    >
                      <option value="">-- Select Manager --</option>
                      {employees.filter(e => e.id !== employee.id).map(mgr => (
                        <option key={mgr.id} value={mgr.id}>{mgr.name} ({mgr.role})</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <hr className="border-slate-100" />

              <AccountAccessSection employee={employee} form={form} settings={settings} updateEmployee={updateEmployee} onUpdate={onUpdate} />

              <hr className="border-slate-100" />

              <div>
                <div className="flex justify-between items-end mb-4">
                  <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
                    <Laptop size={16} className="text-indigo-600" />
                    Assigned Tools & Training Status
                    <InfoTooltip text="Tools this employee has access to." />
                  </h3>
                  <span className="text-xs bg-indigo-50 text-indigo-700 px-2 py-1 rounded font-bold">
                    {assignedTools.length} Tools
                  </span>
                </div>
                <div className="space-y-3">
                  {assignedTools.length === 0 ? (
                    <EmptyState
                      icon={<Laptop size={40} />}
                      title="No tools assigned"
                      subtitle="Assign tools from the Inventory page to track this employee's access and training."
                    />
                  ) : (
                    assignedTools.map(tool => {
                      const trainingRecord = trainingRecords.find(r => r.employeeId === employee.id && r.toolId === tool.id);
                      const isTrained = !!trainingRecord;
                      const needsTraining = !!(tool.trainingUrl && !isTrained);
                      const isOwner = tool.powerUserId === employee.id;
                      const feedback = tool.feedback?.filter(f => f.userId === employee.id).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];
                      return (
                        <div key={tool.id} className="p-3 border border-slate-200 rounded-lg bg-white flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center font-bold text-slate-500 text-xs uppercase shrink-0">
                              {tool.name.substring(0, 2)}
                            </div>
                            <div className="min-w-0">
                              <h4 className="font-semibold text-slate-800 text-sm">{tool.name}</h4>
                              <p className="text-xs text-slate-500">{tool.category}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 text-xs flex-wrap">
                            <div className="flex flex-col items-end">
                              {feedback ? (
                                <>
                                  <span className="text-slate-500">Last Score:</span>
                                  <div className="font-medium text-slate-700">
                                    Sat: <span className="font-bold">{feedback.satisfaction}</span> | Prof: <span className="font-bold">{feedback.proficiency}</span>
                                  </div>
                                </>
                              ) : (
                                <span className="text-slate-400 italic text-xs">No feedback yet</span>
                              )}
                            </div>
                            <div className="shrink-0">
                              {needsTraining ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                                  <GraduationCap size={12} /> Training required
                                </span>
                              ) : tool.trainingUrl ? (
                                <span className="inline-flex items-center gap-1 text-green-600 font-bold text-xs">
                                  <CheckCircle2 size={14} /> Trained
                                </span>
                              ) : (
                                <span className="text-slate-300">-</span>
                              )}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              {!isOwner && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    // Un seul responsable par outil : powerUserId remplace l’owner actuel
                                    const assigned = (tool.assignedUserIds || []).includes(employee.id) ? (tool.assignedUserIds || []) : [...(tool.assignedUserIds || []), employee.id];
                                    onUpdateTool({ ...tool, powerUserId: employee.id, assignedUserIds: assigned });
                                  }}
                                  className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                                  title="Set as sole owner (replaces current owner if any)"
                                >
                                  <ShieldCheck size={12} /> Make owner
                                </button>
                              )}
                              {isOwner && (
                                <span className="text-xs text-indigo-600 font-medium px-2 py-0.5 bg-indigo-50 rounded">Owner</span>
                              )}
                              <button
                                type="button"
                                onClick={() => {
                                  const assigned = (tool.assignedUserIds || []).filter(id => id !== employee.id);
                                  const newPowerUserId = tool.powerUserId === employee.id ? undefined : tool.powerUserId;
                                  onUpdateTool({ ...tool, assignedUserIds: assigned, powerUserId: newPowerUserId });
                                }}
                                className="p-1 rounded text-slate-400 hover:text-red-600 hover:bg-red-50"
                                title="Remove tool"
                              >
                                <X size={16} />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                {tools.filter(t => !(t.assignedUserIds || []).includes(employee.id)).length > 0 && (
                  <div className="flex gap-2 items-center mt-4">
                    <select
                      id={`add-tool-${employee.id}`}
                      className="flex-1 min-w-0 p-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none bg-white"
                      defaultValue=""
                    >
                      <option value="">Select a tool to add…</option>
                      {tools.filter(t => !(t.assignedUserIds || []).includes(employee.id)).map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                      onClick={() => {
                        const sel = document.getElementById(`add-tool-${employee.id}`) as HTMLSelectElement | null;
                        if (!sel?.value) return;
                        const tool = tools.find(t => t.id === sel.value);
                        if (!tool) return;
                        const assigned = [...(tool.assignedUserIds || []), employee.id];
                        onUpdateTool({ ...tool, assignedUserIds: assigned });
                        sel.value = '';
                      }}
                    >
                      <Plus size={16} /> Add
                    </button>
                  </div>
                )}
              </div>
            </>
          )}

          {activeTab === 'reports' && (
            <div>
              <div className="flex justify-between items-end mb-4">
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
                  <User size={16} className="text-slate-400" />
                  Managed Employees
                </h3>
              </div>
              {directReports.length === 0 ? (
                <div className="p-8 text-center bg-slate-50 rounded-lg border border-dashed border-slate-200 text-slate-400 text-sm">
                  {employee.name} does not manage anyone directly.
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4">
                  {directReports.map(report => {
                    const pendingTraining = tools.filter(t => {
                      const assigned = t.assignedUserIds?.includes(report.id) || t.powerUserId === report.id;
                      const needs = !!t.trainingUrl;
                      const done = trainingRecords.some(r => r.employeeId === report.id && r.toolId === t.id);
                      return assigned && needs && !done;
                    });
                    const pendingRequests = notifications.filter(n => n.requesterId === report.id && n.status === 'PENDING');
                    return (
                      <div key={report.id} className="p-4 bg-white border border-slate-200 rounded-xl hover:border-indigo-200 transition-colors">
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center font-bold">
                              {report.name.charAt(0)}
                            </div>
                            <div>
                              <h4 className="font-bold text-slate-800">{report.name}</h4>
                              <p className="text-xs text-slate-500">{report.role}</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            {pendingTraining.length > 0 && (
                              <div className="bg-amber-50 text-amber-700 px-2 py-1 rounded text-xs font-bold flex items-center gap-1">
                                <GraduationCap size={12} /> {pendingTraining.length} Training
                              </div>
                            )}
                            {pendingRequests.length > 0 && (
                              <div className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs font-bold flex items-center gap-1">
                                <Bell size={12} /> {pendingRequests.length} Requests
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div className="bg-slate-50 p-3 rounded-lg">
                            <span className="block text-xs font-bold text-slate-400 uppercase mb-2 flex items-center gap-1">Training Gaps <InfoTooltip text="Required training this employee has not completed yet." /></span>
                            {pendingTraining.length === 0 ? (
                              <span className="text-emerald-500 text-xs flex items-center gap-1"><CheckCircle size={12} /> Training complete</span>
                            ) : (
                              <div className="space-y-1">
                                {pendingTraining.slice(0, 3).map(t => (
                                  <div key={t.id} className="flex items-center gap-1.5 text-slate-700">
                                    <AlertCircle size={12} className="text-amber-500" />
                                    <span className="truncate">{t.name}</span>
                                  </div>
                                ))}
                                {pendingTraining.length > 3 && <span className="text-xs text-slate-400 pl-4">+{pendingTraining.length - 3} more</span>}
                              </div>
                            )}
                          </div>
                          <div className="bg-slate-50 p-3 rounded-lg">
                            <span className="block text-xs font-bold text-slate-400 uppercase mb-2">Active Requests</span>
                            {pendingRequests.length === 0 ? (
                              <span className="text-slate-400 text-xs flex items-center gap-1"><Clock size={12} /> No open requests</span>
                            ) : (
                              <div className="space-y-1">
                                {pendingRequests.slice(0, 3).map(n => (
                                  <div key={n.id} className="flex items-center gap-1.5 text-slate-700">
                                    <FileText size={12} className="text-blue-500" />
                                    <span className="truncate">{n.type === 'NEW_TOOL_REQUEST' ? 'New Tool' : n.toolName || 'Access'}</span>
                                  </div>
                                ))}
                                {pendingRequests.length > 3 && <span className="text-xs text-slate-400 pl-4">+{pendingRequests.length - 3} more</span>}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const EmployeeView: React.FC<EmployeeViewProps> = ({ employees, tools, onAdd, onUpdate, onDelete, onUpdateTool, trainingRecords, settings, notifications }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [isAdding, setIsAdding] = useState(false);
  const [newEmployee, setNewEmployee] = useState<Partial<Employee>>({
    firstName: '',
    lastName: '',
    name: '',
    email: '',
    role: '',
    team: (settings?.teams && settings.teams[0]) || '',
    authRole: 'user',
  });
  
  const [showFilters, setShowFilters] = useState(false);
  const [roleFilter, setRoleFilter] = useState('All');
  const [teamFilter, setTeamFilter] = useState('All');
  
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [sortConfig, setSortConfig] = useState<{ field: SortField, direction: 'asc' | 'desc' }>({
      field: 'name',
      direction: 'asc'
  });

  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);

  const uniqueRoles = Array.from(new Set(employees.map(e => e.role))).sort();

  const filteredEmployees = employees
    .filter(e => {
        const matchesSearch = e.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                              e.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
                              (e.team && e.team.toLowerCase().includes(searchTerm.toLowerCase()));
        const matchesRole = roleFilter === 'All' || e.role === roleFilter;
        const matchesTeam = teamFilter === 'All' || e.team === teamFilter;
        return matchesSearch && matchesRole && matchesTeam;
    })
    .sort((a, b) => {
        let valA: any = a[sortConfig.field as keyof Employee];
        let valB: any = b[sortConfig.field as keyof Employee];

        // Custom sort for tools count
        if (sortConfig.field === 'toolsCount') {
            const countA = tools.filter(t => t.assignedUserIds?.includes(a.id) || t.powerUserId === a.id).length;
            const countB = tools.filter(t => t.assignedUserIds?.includes(b.id) || t.powerUserId === b.id).length;
            valA = countA;
            valB = countB;
        } else if (typeof valA === 'string') {
            valA = valA.toLowerCase();
            valB = valB.toLowerCase();
        }

        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    });

  const handleSort = (field: SortField) => {
    setSortConfig(current => ({
        field,
        direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc'
    }));
    setShowSortMenu(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newEmployee.firstName && newEmployee.lastName && newEmployee.email && newEmployee.role) {
      const fullName = buildFullName(newEmployee.firstName, newEmployee.lastName);
      onAdd({
        id: crypto.randomUUID(),
        name: fullName,
        firstName: newEmployee.firstName,
        lastName: newEmployee.lastName,
        email: newEmployee.email,
        role: newEmployee.role,
        team: newEmployee.team,
        authRole: newEmployee.authRole || 'user',
      });
      setNewEmployee({
        firstName: '',
        lastName: '',
        name: '',
        email: '',
        role: '',
        team: (settings?.teams && settings.teams[0]) || '',
        authRole: 'user',
      });
      setIsAdding(false);
    }
  };

  const handleCsvImport = (data: string[][]) => {
    if (data.length < 2) return;

    const headers = data[0].map(h => h.toLowerCase());
    const rows = data.slice(1);
    let successCount = 0;

    rows.forEach(row => {
        if (row.length < 2) return;

        const getValue = (keys: string[]) => {
            const index = headers.findIndex(h => keys.some(k => h.includes(k)));
            return index !== -1 ? row[index] : '';
        };

        const firstName = getValue(['first name', 'firstname', 'given name']);
        const lastName = getValue(['last name', 'lastname', 'family name', 'surname']);
        const name = getValue(['name', 'full name', 'employee']);
        const email = getValue(['email', 'address']);
        const role = getValue(['role', 'job', 'title']);
        const team = getValue(['team', 'department', 'group']);
        const parsed = splitFullName(name);
        const effectiveFirstName = (firstName || parsed.firstName).trim();
        const effectiveLastName = (lastName || parsed.lastName).trim();

        if (!effectiveFirstName || !effectiveLastName || !email) return;

        // Check for duplicates based on email
        if (employees.some(e => e.email.toLowerCase() === email.toLowerCase())) return;

        onAdd({
            id: crypto.randomUUID(),
            name: buildFullName(effectiveFirstName, effectiveLastName),
            firstName: effectiveFirstName,
            lastName: effectiveLastName,
            email,
            role: role || 'Team Member',
            team: team || undefined
        });
        successCount++;
    });

    if (successCount > 0) {
        alert(`Successfully imported ${successCount} employees.`);
    } else {
        alert('No valid employees found. Ensure headers include First Name, Last Name (or Name), and Email.');
    }
  };

  const downloadTemplate = () => {
    const headers = "First Name,Last Name,Email,Role,Team";
    const example = "John,Doe,john@company.com,Sales Manager,Sales\nJane,Smith,jane@company.com,Developer,Engineering";
    const blob = new Blob([`${headers}\n${example}`], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'employee_import_template.csv';
    a.click();
  };

  const getManagerName = (managerId?: string) => {
      if (!managerId) return '-';
      return employees.find(e => e.id === managerId)?.name || 'Unknown';
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
            <h2 className="text-2xl font-bold text-slate-800">Team Directory</h2>
            <p className="text-slate-500">Manage employees, assign managers, and track tool usage.</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap justify-end">
             {/* View Toggle */}
             <div className="bg-white p-1 rounded-lg border border-slate-200 flex items-center shadow-sm">
                <button 
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-md transition-all ${viewMode === 'list' ? 'bg-toggle text-white' : 'text-slate-400 hover:text-slate-600'}`}
                    title="List View"
                >
                    <List size={18} />
                </button>
                <button 
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-md transition-all ${viewMode === 'grid' ? 'bg-toggle text-white' : 'text-slate-400 hover:text-slate-600'}`}
                    title="Grid View"
                >
                    <LayoutGrid size={18} />
                </button>
             </div>

             {/* Filter & Sort Buttons */}
             <button 
                onClick={() => setShowFilters(!showFilters)}
                className={`px-3 py-2 rounded-lg border text-sm font-medium flex items-center gap-2 transition-colors ${showFilters ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'}`}
            >
                <Filter size={16} />
            </button>

            <div className="relative">
                <button 
                    onClick={() => setShowSortMenu(!showSortMenu)}
                    className="px-3 py-2 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 text-sm font-medium flex items-center gap-2 transition-colors"
                >
                    <ArrowUpDown size={16} />
                </button>
                {showSortMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-slate-100 py-1 z-20">
                        <div className="px-3 py-2 text-xs font-bold text-slate-400 uppercase tracking-wider">Sort By</div>
                        {[
                            { label: 'Name', key: 'name' },
                            { label: 'Role', key: 'role' },
                            { label: 'Team', key: 'team' },
                            { label: 'Tools Count', key: 'toolsCount' }
                        ].map((option) => (
                            <button
                                key={option.key}
                                onClick={() => handleSort(option.key as SortField)}
                                className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex justify-between items-center"
                            >
                                {option.label}
                                {sortConfig.field === option.key && (
                                    <span className="text-indigo-600 text-xs font-bold bg-indigo-50 px-1.5 rounded">
                                        {sortConfig.direction === 'asc' ? 'ASC' : 'DESC'}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <CsvImporter onImport={handleCsvImport} label="Import" />
             <button 
                onClick={downloadTemplate}
                className="text-slate-500 hover:text-indigo-600 text-xs flex items-center gap-1 px-2"
                title="Download CSV Template"
            >
                <Download size={14} />
            </button>
            <button 
            onClick={() => setIsAdding(!isAdding)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-sm text-sm font-medium"
            >
            <Plus size={18} />
            New
            </button>
        </div>
      </div>

      {isAdding && (
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
          <h3 className="text-sm font-bold text-slate-900 mb-4 uppercase">New Employee Details</h3>
          <form onSubmit={handleSubmit} className="flex flex-col md:flex-row gap-4 items-end">
            <div className="flex-1 w-full">
              <label htmlFor="new-emp-first-name" className="block text-xs font-medium text-slate-700 mb-1">First Name</label>
              <input 
                id="new-emp-first-name"
                type="text" 
                required
                className="w-full p-2 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500"
                value={newEmployee.firstName}
                onChange={e => { const v = e.target.value; setNewEmployee(prev => ({...prev, firstName: v})); }}
                placeholder="Jane"
              />
            </div>
            <div className="flex-1 w-full">
              <label htmlFor="new-emp-last-name" className="block text-xs font-medium text-slate-700 mb-1">Last Name</label>
              <input 
                id="new-emp-last-name"
                type="text" 
                required
                className="w-full p-2 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500"
                value={newEmployee.lastName}
                onChange={e => { const v = e.target.value; setNewEmployee(prev => ({...prev, lastName: v})); }}
                placeholder="Doe"
              />
            </div>
            <div className="flex-1 w-full">
              <label htmlFor="new-emp-email" className="block text-xs font-medium text-slate-700 mb-1">Email Address</label>
              <input 
                id="new-emp-email"
                type="email" 
                required
                className="w-full p-2 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500"
                value={newEmployee.email}
                onChange={e => { const v = e.target.value; setNewEmployee(prev => ({...prev, email: v})); }}
                placeholder="jane@company.com"
              />
            </div>
            <div className="flex-1 w-full">
              <label htmlFor="new-emp-role" className="block text-xs font-medium text-slate-700 mb-1">Role / Job Title</label>
              <input 
                id="new-emp-role"
                type="text" 
                required
                className="w-full p-2 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500"
                value={newEmployee.role}
                onChange={e => { const v = e.target.value; setNewEmployee(prev => ({...prev, role: v})); }}
                placeholder="Marketing Lead"
              />
            </div>
            <div className="flex-1 w-full">
              <label htmlFor="new-emp-team" className="block text-xs font-medium text-slate-700 mb-1">Team</label>
              <select 
                id="new-emp-team"
                className="w-full p-2 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                value={newEmployee.team || ''}
                onChange={e => { const v = e.target.value; setNewEmployee(prev => ({...prev, team: v})); }}
              >
                  <option value="">-- Select Team --</option>
                  {(settings?.teams || []).map(t => (
                      <option key={t} value={t}>{t}</option>
                  ))}
              </select>
            </div>
            <div className="w-32">
              <label className="block text-xs font-medium text-slate-700 mb-1">Access</label>
              <select
                className="w-full p-2 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                value={newEmployee.authRole || 'user'}
                onChange={e => { const v = e.target.value as 'admin' | 'user'; setNewEmployee(prev => ({...prev, authRole: v})); }}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="flex gap-2">
                <button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg font-medium transition-colors">Save</button>
                <button type="button" onClick={() => setIsAdding(false)} className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2 rounded-lg font-medium transition-colors">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Filter Panel */}
      {showFilters && (
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 animate-in slide-in-from-top-2 duration-200">
              <div className="flex flex-wrap gap-4 items-center">
                  <div className="flex-1 min-w-[200px]">
                      <label className="block text-xs font-bold text-slate-500 mb-1">Filter by Team</label>
                      <select 
                        value={teamFilter}
                        onChange={(e) => setTeamFilter(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white text-sm"
                    >
                        <option value="All">All Teams</option>
                        {(settings?.teams || []).map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="flex-1 min-w-[200px]">
                      <label className="block text-xs font-bold text-slate-500 mb-1">Filter by Role</label>
                      <select 
                        value={roleFilter}
                        onChange={(e) => setRoleFilter(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white text-sm"
                    >
                        <option value="All">All Roles</option>
                        {uniqueRoles.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  <div className="flex items-end">
                      <button 
                        onClick={() => { setRoleFilter('All'); setTeamFilter('All'); }}
                        className="px-3 py-2 text-sm text-slate-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-1"
                      >
                          <X size={14} /> Clear
                      </button>
                  </div>
              </div>
          </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
            <input 
              type="text" 
              placeholder="Search by name, role or team..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
            />
          </div>
        </div>

        {filteredEmployees.length === 0 ? (
            <EmptyState
              icon={<User size={40} />}
              title="No employees yet"
              subtitle="Add your team members to start assigning tools and tracking training."
              action={{ label: 'Add employee', onClick: () => setIsAdding(true) }}
            />
        ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
                {filteredEmployees.map(employee => {
                    const managedToolsCount = tools.filter(t => t.powerUserId === employee.id).length;
                    return (
                        <div 
                            key={employee.id} 
                            onClick={() => setSelectedEmployee(employee)}
                            className="flex items-start gap-4 p-4 rounded-xl border border-slate-100 hover:border-indigo-100 hover:shadow-md transition-all group bg-slate-50/50 cursor-pointer"
                        >
                            <div className="w-12 h-12 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center font-bold text-lg">
                                {employee.name.charAt(0)}
                            </div>
                            <div className="flex-1 min-w-0">
                                <h4 className="font-bold text-slate-800 truncate">{employee.name}</h4>
                                <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                                    <Briefcase size={12} />
                                    <span className="truncate">{employee.role}</span>
                                    {employee.team && (
                                        <span className="bg-slate-100 px-1.5 py-0.5 rounded text-slate-600 font-medium">
                                            {employee.team}
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                                    <Mail size={12} />
                                    <span className="truncate">{employee.email}</span>
                                </div>
                                {managedToolsCount > 0 && (
                                    <div className="mt-3 inline-flex items-center gap-1.5 px-2 py-1 rounded bg-indigo-50 text-indigo-700 text-[10px] font-bold">
                                        <ShieldCheck size={12} />
                                        {managedToolsCount} Tools Managed
                                    </div>
                                )}
                            </div>
                            <button 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete(employee.id);
                                }}
                                className="opacity-0 group-hover:opacity-100 p-2 text-slate-400 hover:text-red-500 transition-opacity"
                                title="Delete Employee"
                            >
                                <Trash2 size={16} />
                            </button>
                        </div>
                    );
                })}
            </div>
        ) : (
            <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider font-semibold">
                            <th 
                                className="px-6 py-4 cursor-pointer hover:bg-slate-100"
                                onClick={() => handleSort('name')}
                            >
                                <div className="flex items-center gap-1">Employee {sortConfig.field === 'name' && <ArrowUpDown size={12}/>}</div>
                            </th>
                            <th 
                                className="px-6 py-4 cursor-pointer hover:bg-slate-100"
                                onClick={() => handleSort('role')}
                            >
                                <div className="flex items-center gap-1">Role {sortConfig.field === 'role' && <ArrowUpDown size={12}/>}</div>
                            </th>
                            <th 
                                className="px-6 py-4 cursor-pointer hover:bg-slate-100"
                                onClick={() => handleSort('team')}
                            >
                                <div className="flex items-center gap-1">Team {sortConfig.field === 'team' && <ArrowUpDown size={12}/>}</div>
                            </th>
                            <th className="px-6 py-4">Manager</th>
                            <th className="px-6 py-4 text-center">Tools Used</th>
                            <th className="px-6 py-4 text-center">Tools Managed</th>
                            <th className="px-6 py-4 text-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {filteredEmployees.map(employee => {
                            const managedToolsCount = tools.filter(t => t.powerUserId === employee.id).length;
                            const usedToolsCount = tools.filter(t => t.assignedUserIds?.includes(employee.id) || t.powerUserId === employee.id).length;
                            return (
                                <tr 
                                    key={employee.id} 
                                    onClick={() => setSelectedEmployee(employee)}
                                    className="hover:bg-slate-50 transition-colors cursor-pointer group"
                                >
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center font-bold text-xs">
                                                {employee.name.charAt(0)}
                                            </div>
                                            <div>
                                                <div className="font-medium text-slate-800 group-hover:text-indigo-700 transition-colors">{employee.name}</div>
                                                <div className="text-xs text-slate-500">{employee.email}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-slate-600">{employee.role}</td>
                                    <td className="px-6 py-4 text-sm text-slate-600">
                                        {employee.team ? (
                                            <span className="bg-slate-100 px-2 py-1 rounded text-xs font-medium">{employee.team}</span>
                                        ) : '-'}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-slate-600">{getManagerName(employee.managerId)}</td>
                                    <td className="px-6 py-4 text-sm text-center font-medium text-slate-700">
                                        {usedToolsCount}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-center">
                                        {managedToolsCount > 0 ? (
                                             <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-xs font-bold">
                                                {managedToolsCount}
                                            </span>
                                        ) : (
                                            <span className="text-slate-400">-</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <button 
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onDelete(employee.id);
                                            }}
                                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                            title="Delete"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        )}
      </div>

      {selectedEmployee && (
        <EmployeeModal 
            employee={employees.find(e => e.id === selectedEmployee.id) ?? selectedEmployee} 
            onClose={() => setSelectedEmployee(null)}
            employees={employees}
            tools={tools}
            trainingRecords={trainingRecords}
            settings={settings}
            notifications={notifications}
            onUpdate={onUpdate}
            onUpdateTool={onUpdateTool}
        />
      )}
    </div>
  );
};